import { type Audience, FEATURES } from "@/content";

type FeaturesProps = { audience: Audience };

/**
 * Product / solution grid. Features are filtered by audience — the printer-only
 * ones (catalog validation, prepress QC) don't show for a broker, who isn't a
 * print shop. Each card gets its own hue via `feature.tint`.
 */
export function Features({ audience }: FeaturesProps) {
  const items = FEATURES.filter((feature) => !feature.only || feature.only === audience);
  const lgCols = items.length >= 6 ? "lg:grid-cols-3" : "lg:grid-cols-2";

  return (
    <section id="solucion" className="border-t border-[var(--separator)] py-20 sm:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-wider text-[var(--muted)]">
            La solución
          </p>
          <h2 className="mt-3 text-balance text-3xl font-bold tracking-tight text-[var(--foreground)] sm:text-4xl">
            Todo tu trabajo ordenado, en un solo lugar.
          </h2>
          <p className="mt-4 text-lg text-[var(--muted)]">
            No es otra planilla ni otro grupo de WhatsApp: es el sistema nervioso de tu operación,
            del primer contacto a la entrega.
          </p>
        </div>

        <div className={`mt-12 grid gap-5 sm:grid-cols-2 ${lgCols}`}>
          {items.map((feature) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.title}
                className="rounded-2xl border border-[var(--separator)] bg-[var(--surface)] p-7 transition-colors hover:border-[color-mix(in_oklch,var(--foreground)_18%,transparent)]"
              >
                <div
                  className="flex h-12 w-12 items-center justify-center rounded-xl"
                  style={{ background: `color-mix(in oklch, ${feature.tint} 16%, transparent)` }}
                >
                  <Icon className="h-6 w-6" style={{ color: feature.tint }} />
                </div>
                <div className="mt-5 flex flex-wrap items-center gap-2">
                  <h3 className="text-xl font-semibold text-[var(--foreground)]">
                    {feature.title}
                  </h3>
                  {feature.badge && (
                    <span
                      className="rounded-full border px-2 py-0.5 text-[11px] font-medium"
                      style={{
                        borderColor: `color-mix(in oklch, ${feature.tint} 30%, transparent)`,
                        background: `color-mix(in oklch, ${feature.tint} 12%, transparent)`,
                        color: feature.tint,
                      }}
                    >
                      {feature.badge}
                    </span>
                  )}
                </div>
                <p className="mt-2 text-[15px] leading-relaxed text-[var(--muted)]">
                  {feature.body}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
